import { useEffect, useId, useState } from 'react'
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { imageProcessingService, type NormalizedImage } from '../../services/imageProcessingService'
import { useTheme } from '../../contexts/ThemeContext'
import { withAlpha } from '../../utils/themeUtils'
import { Button } from '../ui/button'

type CaptureImagePickerProps = {
  value: NormalizedImage | null
  disabled?: boolean
  onChange: (image: NormalizedImage | null) => void
}

const formatBytes = (bytes: number) => `${Math.max(1, Math.round(bytes / 1024))} KB`

export function CaptureImagePicker({ value, disabled, onChange }: CaptureImagePickerProps) {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const inputId = useId()
  const [isPreparing, setIsPreparing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!value) {
      setPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(value.blob)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [value])

  const chooseImage = async (file: File | undefined) => {
    if (!file) return
    setIsPreparing(true)
    setError(null)
    try {
      onChange(await imageProcessingService.normalizeCaptureImage(file))
    } catch {
      onChange(null)
      setError(t('core.capture.image.error'))
    } finally {
      setIsPreparing(false)
    }
  }

  if (value && previewUrl) {
    return (
      <div className="overflow-hidden rounded-2xl border" style={{ borderColor: withAlpha(colors.border, 'B0') }}>
        <img
          src={previewUrl}
          alt={t('core.capture.image.previewAlt')}
          className="max-h-64 w-full bg-black/5 object-contain"
        />
        <div className="flex items-center justify-between gap-3 px-3 py-2.5">
          <p className="text-xs" style={{ color: colors.textSecondary }}>
            {t('core.capture.image.ready', {
              size: formatBytes(value.byteSize),
              width: value.width,
              height: value.height,
            })}
          </p>
          <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => onChange(null)}>
            <XMarkIcon className="h-4 w-4" />
            {t('core.capture.image.remove')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="sr-only"
        disabled={disabled || isPreparing}
        onChange={(event) => {
          void chooseImage(event.target.files?.[0])
          event.target.value = ''
        }}
      />
      <label
        htmlFor={inputId}
        className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-xl px-3 text-sm font-semibold transition-opacity hover:opacity-80"
        style={{ color: colors.primary, backgroundColor: withAlpha(colors.primary, '10') }}
      >
        <PhotoIcon className="h-5 w-5" />
        {isPreparing ? t('core.capture.image.preparing') : t('core.capture.image.add')}
      </label>
      {error ? <p role="alert" className="mt-2 text-xs" style={{ color: colors.error }}>{error}</p> : null}
    </div>
  )
}
